import { DestroyRef, Injectable, InjectionToken, NgZone, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, defer, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Result } from '../models/result.model';
import { ContextoAcesso, EntradaAcessoResponse, EntradaCredenciaisRequest, entradaPermitida } from './acesso.model';
import { ERRO_ENTRADA_TRATADO } from './acesso-http';

/** Relógio substituível na etapa de testes, sem configuração de executor nesta feature. */
export const RELOGIO_ACESSO = new InjectionToken<() => number>('RELOGIO_ACESSO', {
  providedIn: 'root', factory: () => () => Date.now()
});
export const PRAZO_INATIVIDADE_MS = 20 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AcessoService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly agora = inject(RELOGIO_ACESSO);
  private readonly estado = signal<ContextoAcesso | null>(null);
  private readonly desconectado = signal(false);
  private avisoPendente: string | null = null;
  private tokenAcesso: string | null = null;
  private ultimaAtividade = 0;
  private temporizador: ReturnType<typeof setTimeout> | undefined;
  private readonly baseUrl = `${environment.apiUrl.replace(/\/$/, '')}/api/acesso/entrada`;

  readonly contexto = this.estado.asReadonly();
  readonly naoConectado = this.desconectado.asReadonly();

  constructor() {
    const atividade = (evento: Event): void => {
      if (!evento.isTrusted) return;
      this.zone.run(() => this.registrarAtividade());
    };
    const verificar = (): void => { this.zone.run(() => this.verificarInatividade()); };
    // Interação humana, não respostas HTTP, mantém o contexto em memória ativo.
    const eventos = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
    this.zone.runOutsideAngular(() => {
      eventos.forEach(nome => this.document.addEventListener(nome, atividade, { passive: true }));
      this.document.addEventListener('visibilitychange', verificar);
      this.document.defaultView?.addEventListener('focus', verificar);
    });
    inject(DestroyRef).onDestroy(() => {
      eventos.forEach(nome => this.document.removeEventListener(nome, atividade));
      this.document.removeEventListener('visibilitychange', verificar);
      this.document.defaultView?.removeEventListener('focus', verificar);
      this.cancelarTemporizador();
    });
  }

  entrarWindows(): Observable<Result<EntradaAcessoResponse>> {
    return this.entrada(() => this.http.post<Result<EntradaAcessoResponse>>(this.baseUrl, null, {
      context: new HttpContext().set(ERRO_ENTRADA_TRATADO, true)
    }));
  }

  entrarCredenciais(dados: EntradaCredenciaisRequest): Observable<Result<EntradaAcessoResponse>> {
    return this.entrada(() => this.http.post<Result<EntradaAcessoResponse>>(`${this.baseUrl}/credenciais`, dados, {
      context: new HttpContext().set(ERRO_ENTRADA_TRATADO, true)
    }));
  }

  private entrada(requisicao: () => Observable<Result<EntradaAcessoResponse>>): Observable<Result<EntradaAcessoResponse>> {
    return defer(() => {
      this.limparContexto(false);
      return requisicao().pipe(tap(resultado => {
        if (!entradaPermitida(resultado)) throw new Error('Erro inesperado');
        const { token, ...contexto } = resultado.data;
        this.estado.set(contexto);
        this.tokenAcesso = token;
        this.avisoPendente = resultado.data.avisoValidade;
        this.ultimaAtividade = this.agora();
        this.agendarExpiracao();
      }));
    });
  }

  obterContexto(): ContextoAcesso | null {
    this.verificarInatividade();
    return this.estado();
  }

  /** Token em memória, enviado como Bearer pelo interceptor. Nulo sem entrada ou após inatividade. */
  obterToken(): string | null {
    this.verificarInatividade();
    return this.estado() ? this.tokenAcesso : null;
  }

  /** Troca o token pelo reemitido pelo servidor; o prazo de inatividade segue valendo no cliente. */
  renovarToken(token: string): void {
    if (this.estado() && token.trim()) this.tokenAcesso = token;
  }

  consumirAvisoValidade(): string | null {
    if (!this.obterContexto()) return null;
    const aviso = this.avisoPendente;
    this.avisoPendente = null;
    return aviso;
  }

  limparContexto(apresentarDesconexao = true): void {
    this.cancelarTemporizador();
    this.estado.set(null);
    this.tokenAcesso = null;
    this.avisoPendente = null;
    this.ultimaAtividade = 0;
    this.desconectado.set(apresentarDesconexao);
  }

  registrarAtividade(): void {
    this.verificarInatividade();
    if (!this.estado()) return;
    this.ultimaAtividade = this.agora();
    this.agendarExpiracao();
  }

  verificarInatividade(): void {
    if (this.estado() && this.agora() - this.ultimaAtividade >= PRAZO_INATIVIDADE_MS) {
      this.limparContexto();
    }
  }

  private agendarExpiracao(): void {
    this.cancelarTemporizador();
    const restante = Math.max(0, PRAZO_INATIVIDADE_MS - (this.agora() - this.ultimaAtividade));
    this.zone.runOutsideAngular(() => {
      this.temporizador = setTimeout(() => this.zone.run(() => {
        this.verificarInatividade();
        if (this.estado()) this.agendarExpiracao();
      }), restante);
    });
  }

  private cancelarTemporizador(): void {
    if (this.temporizador !== undefined) clearTimeout(this.temporizador);
    this.temporizador = undefined;
  }
}
